import React, { useEffect, useState, useMemo } from "react";
import { getUserRecommandation } from "../../../api";
import { get } from "lodash";
import styles from "./styles.m.css";
import ResponsiveImageWithFallback from "../responsive-image-with-fallback";
import { generateImageSources } from "../../utils/utils";
import { DeepBI } from "../../../integrations";
import { getParentSection } from "../../../data/section";

const STORAGE_KEY = "recommendation_widget_data";
const STORAGE_DATE_KEY = "recommendation_widget_date";

// ✅ Safe LocalStorage wrapper
const storage = {
  load: () => {
    if (!global.localStorage) return null;
    try {
      const storedData = global.localStorage.getItem(STORAGE_KEY);
      const storedDate = global.localStorage.getItem(STORAGE_DATE_KEY);
      if (storedData && storedDate) {
        const today = new Date().toISOString().split("T")[0];
        if (storedDate === today) {
          return JSON.parse(storedData);
        }
      }
    } catch (err) {
      console.error("Failed to load from localStorage:", err);
    }
    return null;
  },
  save: stories => {
    if (!global.localStorage) return;
    try {
      const today = new Date().toISOString().split("T")[0];
      global.localStorage.setItem(STORAGE_KEY, JSON.stringify(stories));
      global.localStorage.setItem(STORAGE_DATE_KEY, today);
    } catch (err) {
      console.error("Failed to save to localStorage:", err);
    }
  },
  clear: () => {
    if (!global.localStorage) return;
    try {
      global.localStorage.removeItem(STORAGE_KEY);
      global.localStorage.removeItem(STORAGE_DATE_KEY);
    } catch (err) {
      console.error("Failed to clear localStorage:", err);
    }
  }
};

export const RecommendationWidgetInhouse = story => {
  const [reStories, setReStories] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ Fetch recommendations from API
  const fetchRecommendations = async userId => {
    setLoading(true);
    try {
      const response = await getUserRecommandation({ user_id: userId });
      const recommendations = response.recommendations || [];
      setReStories(recommendations);
      storage.save(recommendations);
    } catch (err) {
      console.error("Failed to fetch recommendations:", err);
    }
    setLoading(false);
  };

  // ✅ Fire DeepBI event only when reStories changes
  useEffect(
    () => {
      if (loading) return; // skip while still loading
      if (reStories.length > 0) {
        DeepBI.PingDefaultevents("Rec_Widget_Display", "yes");
      } else {
        DeepBI.PingDefaultevents("Rec_Widget_Display", "no");
      }
    },
    [reStories, loading]
  ); // 👈 triggered only when stories update

  // ✅ Remove story and update storage
  const handleStoryClick = articleId => {
    DeepBI.PingDefaultevents("Rec_Widget_Story_Click", "", articleId);
    const updatedStories = reStories.filter(story => story.article_id !== articleId);
    setReStories(updatedStories);

    if (updatedStories.length > 0) {
      storage.save(updatedStories);
    } else {
      storage.clear();
      // re-fetch fresh data when empty
      if (global.userPromise) {
        global.userPromise.catch(() => ({})).then(user => {
          if (user.userId) fetchRecommendations(user.userId);
        });
      }
    }
  };

  useEffect(() => {
    if (!global.userPromise) {
      setLoading(false);
      return;
    }
    global.userPromise.catch(() => ({})).then(user => {
      if (!user.userId) {
        setLoading(false);
        return;
      }

      const cached = storage.load();

      if (Array.isArray(cached) && cached.length > 0) {
        setReStories(cached);
        setLoading(false);
      } else {
        fetchRecommendations(user.userId);
      }
    });
  }, []);

  const storyList = useMemo(
    () => {
      const sections = get(story, ["story", "sections"], {});
      const parentSectionObj = sections[0] ? getParentSection(sections[0]) : null;
      const parentSec = get(parentSectionObj, ["name"], "");
      const subSection = get(story, ["story", "sections", 0, "name"], "");

      const sortedStories = [...reStories].sort((a, b) => {
        const aSection = a.section || "";
        const bSection = b.section || "";

        const aSubsection = a.subsection || "";
        const bSubsection = b.subsection || "";

        // ------------------------------
        // CASE 1: Parent section exists
        // ------------------------------
        if (parentSec) {
          if (aSection === parentSec && bSection !== parentSec) return -1;
          if (bSection === parentSec && aSection !== parentSec) return 1;
          return 0; // keep original order when both match or both don't
        }

        // ------------------------------
        // CASE 2: Parent section empty → fallback to subsection check
        // ------------------------------
        if (subSection) {
          if (aSubsection === subSection && bSubsection !== subSection) return -1;
          if (bSubsection === subSection && aSubsection !== subSection) return 1;
          return 0;
        }

        // ------------------------------
        // CASE 3: both parent & subsection empty → no sort
        // ------------------------------
        return 0;
      });

      return sortedStories.slice(0, 6).map(story => {
        const headline = get(story, ["headline"], "");
        let slug = get(story, ["slug"], "#");

        if (slug && slug !== "#") {
          slug += slug.includes("?") ? "&pfrom=recommendation-widget" : "?pfrom=recommendation-widget";
        }

        return (
          <li key={story.article_id} className="flex gap-3 p-2 border-b last:border-0 items-center">
            <a href={slug} className={styles["story-grid"]} onClick={() => handleStoryClick(story.article_id)}>
              <div className={styles["image-container"]}>
                <ResponsiveImageWithFallback
                  className={styles["image-wrapper"]}
                  slug={`${story.hero_image_s3_key}`}
                  metadata={story.imageMetadata}
                  alt={story.imageCaption || headline}
                  imgParams={{ auto: ["format", "compress"] }}
                  sources={generateImageSources(
                    { aspectRatio: [3, 2], screenWidthCoverage: 0.3 },
                    { aspectRatio: [16, 9], screenWidthCoverage: 0.1 }
                  )}
                  story={story}
                />
              </div>
              <div className={styles["headline"]}>{headline}</div>
            </a>
          </li>
        );
      });
    },
    [reStories]
  );

  if (loading) return <div>Loading recommendations...</div>;
  if (reStories.length === 0) return <div />;

  return (
    <div className={styles["recommended-wrapper-section"]}>
      <h3 className="text-lg font-semibold mb-2">
        <span>இந்தக் கட்டுரைகளையும் படிக்கலாமே!</span>
      </h3>
      <div className={styles["recommended-wrapper"]}>
        <ul>{storyList}</ul>
      </div>
    </div>
  );
};
